import React from 'react'
import { CreditApplicationBusinessEntity, CreditApplicationBusinessOwnership } from '_gql'
import * as yup from 'yup'

import { FormattedMessage } from '@divvy-web/i18n'

import { maskedInput } from '@sputnik/schema/types'
import { containIdenticalNumbers, einContainsNineConsecutiveNumbers } from '@sputnik/utils/taxIdValidation'

export const legalBusinessName = yup
  .string()
  .matches(/^[a-zA-Z0-9-/&,.' ]*$/, {
    message: () => (
      <FormattedMessage
        defaultMessage="Only the following special characters are allowed: , . / & - '"
        id='sputnik.businessInfoSchema__6vj0/b'
      />
    ),
  })
  .max(200, () => (
    <FormattedMessage
      defaultMessage='Please enter a valid legal business name under 200 characters'
      id='sputnik.businessInfoSchema__Pm6+av'
    />
  ))

export const preferredAccountName = yup
  .string()
  .matches(/^[a-zA-Z0-9-/&,. ]*$/, {
    message: () => (
      <FormattedMessage
        defaultMessage='Only the following special characters are allowed: , . / & -'
        id='sputnik.businessInfoSchema__mShK12'
      />
    ),
  })
  .max(21, () => (
    <FormattedMessage
      defaultMessage='Please enter a valid preferred business name under 21 characters'
      id='sputnik.businessInfoSchema__wXrCB2'
    />
  ))

export const website = yup
  .string()
  .nullable()
  .matches(
    new RegExp(
      '^((((http|https):\\/{2})*(([0-9a-z_-]+\\.)+([a-zA-Z]{2,})((\\/([~0-9a-zA-Z#\\+%@\\.\\/_-]+))?)?))\\b)?\\/?$',
      'imu',
    ),
    {
      message: () => (
        <FormattedMessage
          defaultMessage='Please enter a valid website'
          id='sputnik.businessInfoSchema__FCz7S9'
        />
      ),
    },
  )

export const formedOn = yup
  .date()
  .typeError(() => (
    <FormattedMessage
      defaultMessage='Please enter a valid formation date. For example, 01/31/1990'
      id='sputnik.businessInfoSchema__nDcQty'
    />
  ))
  .min(new Date('1700-01-01'), () => (
    <FormattedMessage
      defaultMessage='Please enter a date in the 18th century or newer'
      id='sputnik.businessInfoSchema__tTel8e'
    />
  ))
  .max(new Date(), () => (
    <FormattedMessage
      defaultMessage='Please enter a valid date'
      id='sputnik.businessInfoSchema__6DCLcI'
    />
  ))
const invalidTaxIdErrorMessage = () => (
  <FormattedMessage
    defaultMessage='Please enter a valid Tax ID/EIN #'
    id='sputnik.businessInfoSchema__4LwoZX'
  />
)

export const taxId = maskedInput.test('isValid', invalidTaxIdErrorMessage, (input, { path, createError }) => {
  // empty value is valid
  if (!input || !input.value) {
    return true
  }
  if (input.value.length !== 9) {
    return createError({ path: `${path}.display`, message: invalidTaxIdErrorMessage })
  }
  if (!containIdenticalNumbers(input.value) && !einContainsNineConsecutiveNumbers(input.value)) {
    return true
  }
  return createError({ path: `${path}.display`, message: invalidTaxIdErrorMessage })
})

export const numberOfEmployees = yup
  .number()
  .transform((value) => (Number.isNaN(value) ? Number.NaN : value))
  .nullable()
  .typeError(() => (
    <FormattedMessage
      defaultMessage='Please enter a valid number'
      id='sputnik.businessInfoSchema__6zUj8M'
    />
  ))
  .integer(() => (
    <FormattedMessage
      defaultMessage='Please enter a valid integer'
      id='sputnik.businessInfoSchema__JLzSFq'
    />
  ))
  .moreThan(0, () => (
    <FormattedMessage
      defaultMessage='Please enter a valid number greater than 0'
      id='sputnik.businessInfoSchema__3QzURv'
    />
  ))

export const ownership = yup.string().oneOf(Object.values(CreditApplicationBusinessOwnership), () => (
  <FormattedMessage
    defaultMessage='Please choose a valid ownership value'
    id='sputnik.businessInfoSchema__2TqvU2'
  />
))

export const entityType = yup.string().oneOf(Object.values(CreditApplicationBusinessEntity), () => (
  <FormattedMessage
    defaultMessage='Please choose a valid entity type'
    id='sputnik.businessInfoSchema__AsMbNO'
  />
))

export const naicsCode = yup.string().typeError(() => (
  <FormattedMessage
    defaultMessage='Please enter a valid industry'
    id='sputnik.businessInfoSchema__TJHR1d'
  />
))

export const accountingSoftware = yup.string()

export const annualRevenue = yup
  .number()
  .transform((value) => (Number.isNaN(value) ? Number.NaN : value))
  .nullable()
  .typeError('')
  .moreThan(0, () => (
    <FormattedMessage
      defaultMessage='Value must be greater than $0'
      id='sputnik.businessInfoSchema__ByFOAx'
    />
  ))
